import React, { useCallback, useContext, useEffect, useState } from 'react';
import { HELP_DESK_STATUSES, HELP_DESK_PRIORITIES } from '@ubeya/shared/constants/helpDesk';
import styled from 'styled-components';
import { useQueryClient } from 'react-query';
import { ago } from '@ubeya/shared/utils/time';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useHelpDeskEmployeeTickets, {
  getHelpDeskEmployeeTicketsQueryKey
} from '@ubeya/shared/hooks/account/useHelpDeskEmployeeTickets';
import { Flex, FlexCenter, LoaderArea, Button, TextWithTooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseSnoozeIcon } from '@ubeya/shared-web/assets/help-desk/snooze.svg';
import { ReactComponent as PlusIcon } from '@ubeya/shared-web/assets/help-desk/plus-small.svg';
import { getMessagePreviewContent, getSnoozedLabel, getTicketBranch } from '../helpDeskUtils';
import { useHelpDeskAnalytics } from '../helpDeskAnalytics';
import useOpenTicketInSidePanel from '../useOpenTicketInSidePanel';
import CreateTicketModal from './CreateTicket/CreateTicketModal';
import {
  ResolvedBadge,
  ClosedBadge,
  CategoryDisplay,
  AssigneeDisplay,
  BranchDisplay,
  PriorityDisplay,
  UnreadBadge,
  EventLink,
  MetaChip as MetaChipBase,
  chipsRowStyle,
  TicketBadge
} from './shared';
import TicketsHistoryEmptyState from './TicketsHistoryEmptyState';

// region: styled

const TicketCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  cursor: pointer;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey2};

  &:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }
`;

const MetaChip = styled(MetaChipBase)`
  span {
    font-weight: 400;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 28px;
  margin-bottom: 4px;
`;

const TicketNumber = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  flex-shrink: 0;
  margin-inline-start: auto;
`;

const TicketTitle = styled(TextWithTooltip)`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  min-width: 0;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`;

const MessagePreview = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  overflow: hidden;
  font-weight: 400;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const HistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
`;

const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  width: 100%;
  background: ${({ theme }) => theme.colors.grey1};
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  border-radius: 4px;
  padding: 10px;
`;

const SectionTitle = styled.div``;

const CreateButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 34px;
  padding-left: 8px;
  padding-right: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
`;

const LoaderWrapper = styled(FlexCenter)`
  padding: 10px;
`;

const MetaRow = styled(Flex)`
  min-width: 0;
  color: ${({ theme }) => theme.colors.black};
  ${chipsRowStyle}
`;

const TimeAgo = styled.div`
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.grey4};
  margin-right: ${({ $isMarginNeeded }) => ($isMarginNeeded ? '4px' : '0')};
`;

const SnoozeInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-end;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.black};
`;

const SnoozeIcon = styled(BaseSnoozeIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  circle,
  path,
  line {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const SnoozeText = styled.div`
  min-width: 0;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
`;

const SeeMoreLink = styled.div`
  padding: 8px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.link};
  cursor: pointer;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

// region: helpers

// region: component

const PAGE_SIZE = 4;

const TicketsHistorySection = ({
  accountId,
  employeeId,
  showTitle = false,
  isActive = false,
  isDeleted = false,
  onCreateModalToggle,
  openCreatedTicketInSidePanel = true
}) => {
  const { t } = useContext(ConfigContext);
  const { modules } = useModules();
  const { mappedBranches } = useBranches();
  const { trackCreateTicketOpened } = useHelpDeskAnalytics();
  const queryClient = useQueryClient();

  const canCreateTicket = !!modules?.help_desk && isActive && !isDeleted;
  const [offset, setOffset] = useState(0);
  const [allTickets, setAllTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    setOffset(0);
    setAllTickets([]);
    setTotal(0);
  }, [employeeId]);

  useEffect(() => {
    onCreateModalToggle?.(isCreateOpen);
  }, [isCreateOpen, onCreateModalToggle]);

  const handleOpenCreate = useCallback(() => {
    trackCreateTicketOpened('Employee profile - tickets history');
    setIsCreateOpen(true);
  }, [trackCreateTicketOpened]);

  const closeCreateModal = useCallback(() => setIsCreateOpen(false), []);

  const openTicketInSidePanel = useOpenTicketInSidePanel();
  const handleTicketCreated = useCallback(
    (ticket) => {
      setIsCreateOpen(false);
      setOffset(0);
      queryClient.invalidateQueries(getHelpDeskEmployeeTicketsQueryKey(accountId, employeeId));

      if (openCreatedTicketInSidePanel) {
        openTicketInSidePanel(ticket?.id);
      }
    },
    [queryClient, accountId, employeeId, openCreatedTicketInSidePanel, openTicketInSidePanel]
  );

  const { isLoading } = useHelpDeskEmployeeTickets({
    accountId,
    employeeId,
    limit: PAGE_SIZE,
    offset,
    onSuccess: (response) => {
      const newTickets = response?.data ?? [];
      setTotal(response?.total ?? 0);
      setAllTickets((prev) => {
        if (offset === 0) return newTickets;
        // Refetches re-deliver this page, so replace the rows it carries instead of
        // skipping them - otherwise refreshed unread state is dropped on the floor.
        const refreshed = new Map(newTickets.map((ticket) => [ticket.id, ticket]));
        const existingIds = new Set(prev.map((ticket) => ticket.id));
        return [
          ...prev.map((ticket) => refreshed.get(ticket.id) ?? ticket),
          ...newTickets.filter((ticket) => !existingIds.has(ticket.id))
        ];
      });
    }
  });

  const hasMore = allTickets.length < total;

  const handleSeeMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE);
  }, []);

  const createTicketButton = canCreateTicket && (
    <CreateButton type="button" primary onClick={handleOpenCreate}>
      <PlusIcon />
      {t('help_desk_create_ticket')}
    </CreateButton>
  );

  const renderTickets = () => {
    if (isLoading && allTickets.length === 0) {
      return (
        <LoaderWrapper>
          <LoaderArea />
        </LoaderWrapper>
      );
    }

    if (allTickets.length === 0) {
      return <TicketsHistoryEmptyState createTicketButton={createTicketButton} />;
    }

    return (
      <HistoryContainer>
        {allTickets.map((tk) => {
          const timeAgoSource = tk.lastMessageAt ?? tk.createdAt;
          const timeAgo = timeAgoSource ? ago(timeAgoSource, t) : '';
          const previewText = getMessagePreviewContent(
            { content: tk.lastMessagePreview, messageType: tk.lastMessageType },
            t
          );

          const branch = getTicketBranch({ branch: tk.branch, mappedBranches });
          const isClosed = tk.status === HELP_DESK_STATUSES.CLOSED;
          const snoozeLabel =
            tk.status === HELP_DESK_STATUSES.SNOOZED
              ? getSnoozedLabel(tk.snoozedUntil, t, { relativeOnly: true })
              : null;

          return (
            <TicketCard key={tk.id} onClick={() => window.open(`/${accountId}/help-desk/${tk.id}`, '_blank')}>
              {/* Line 1: Title + resolved/closed badges + #ticketNumber */}
              <TitleRow>
                <TicketTitle dir="auto">{tk.title}</TicketTitle>
                <TicketBadge ticket={tk} />
                {isClosed && <ClosedBadge />}
                {!!tk.isResolved && <ResolvedBadge />}
                <TicketNumber>#{tk.ticketNumber}</TicketNumber>
              </TitleRow>

              {/* Line 2: Last message preview + time ago + unread */}
              <MessageRow>
                <MessagePreview>{previewText}</MessagePreview>
                {timeAgo && <TimeAgo $isMarginNeeded={tk.unreadCount || tk.isManuallyUnread}>{timeAgo}</TimeAgo>}
                <UnreadBadge count={tk.unreadCount} isDot={tk.isManuallyUnread} />
              </MessageRow>

              {/* Line 3: Branch, category, assignee, priority and related event chips */}
              <MetaRow>
                {branch && (
                  <MetaChip $roundContent>
                    <BranchDisplay branch={branch} />
                  </MetaChip>
                )}

                {tk.category && (
                  <MetaChip>
                    <CategoryDisplay category={tk.category} />
                  </MetaChip>
                )}

                {tk.assigneeName && (
                  <MetaChip $roundContent>
                    <AssigneeDisplay
                      admin={{ name: tk.assigneeName, image: tk.assigneeAvatarUrl ?? null }}
                      hideNoAssigneeIcon
                    />
                  </MetaChip>
                )}

                {tk.priority && tk.priority !== HELP_DESK_PRIORITIES.NONE && (
                  <MetaChip>
                    <PriorityDisplay priority={tk.priority} />
                  </MetaChip>
                )}

                <EventLink ticket={tk} accountId={accountId} />
              </MetaRow>

              {/* Line 4: Snooze countdown, on its own row below the chips */}
              {snoozeLabel && (
                <SnoozeInfo>
                  <SnoozeIcon />
                  <SnoozeText>{snoozeLabel}</SnoozeText>
                </SnoozeInfo>
              )}
            </TicketCard>
          );
        })}

        {hasMore && <SeeMoreLink onClick={handleSeeMore}>{t('help_desk_see_more')}</SeeMoreLink>}
      </HistoryContainer>
    );
  };

  return (
    <>
      {showTitle && allTickets.length > 0 && (
        <HeaderWrap>
          <SectionTitle>{t('help_desk_tickets_history')}</SectionTitle>
          {createTicketButton}
        </HeaderWrap>
      )}
      {isCreateOpen && (
        <CreateTicketModal lockedEmployeeId={employeeId} onClose={closeCreateModal} onCreated={handleTicketCreated} />
      )}
      {renderTickets()}
    </>
  );
};

export default TicketsHistorySection;
